// ============================================================================
// Comparison ranges & KPI deltas (prior period / same period last year).
// ============================================================================

import { addDays, daysBetween } from '../dateUtils';
import { KpiDelta } from '../../components/analysis/shared/KpiTile';

export type ComparisonMode = 'none' | 'prior' | 'yoy';

export interface DateRange {
  from: string;
  to: string;
}

/** Same calendar date one year earlier; Feb 29 clamps to Feb 28. */
export function shiftYearBack(date: string): string {
  const year = Number(date.slice(0, 4)) - 1;
  const monthDay = date.slice(5);
  const candidate = `${year}-${monthDay}`;
  if (monthDay === '02-29') return `${year}-02-28`;
  return candidate;
}

export function comparisonRange(range: DateRange, mode: ComparisonMode): DateRange | null {
  if (mode === 'none') return null;
  if (mode === 'yoy') {
    return { from: shiftYearBack(range.from), to: shiftYearBack(range.to) };
  }
  const length = daysBetween(range.from, range.to) + 1;
  return { from: addDays(range.from, -length), to: addDays(range.from, -1) };
}

export const COMPARISON_NOTE: Record<Exclude<ComparisonMode, 'none'>, string> = {
  prior: 'vs prior period',
  yoy: 'vs same period last year',
};

/**
 * Build a KpiDelta from current vs prior values. `goodWhenUp` colors the
 * arrow (occupancy up = good; failure rate up = bad).
 */
export function makeDelta(
  current: number | null,
  prior: number | null,
  goodWhenUp = true
): KpiDelta | undefined {
  if (current === null || prior === null) return undefined;
  if (prior === 0) {
    if (current === 0) return { text: 'no change', direction: 'flat' };
    return { text: 'new', direction: 'up', good: goodWhenUp };
  }
  const pct = ((current - prior) / Math.abs(prior)) * 100;
  if (Math.abs(pct) < 0.05) return { text: 'no change', direction: 'flat' };
  const direction = pct > 0 ? 'up' : 'down';
  return {
    text: `${Math.abs(pct) >= 100 ? Math.round(Math.abs(pct)) : Math.abs(pct).toFixed(1)}%`,
    direction,
    good: direction === 'up' ? goodWhenUp : !goodWhenUp,
  };
}
